<template>
	<button
		class="c-button button"
		:class="classes"
		v-bind="$attrs"
	>
		<span
			v-if="$slots.icon"
			class="c-button__icon"
		>
			<slot name="icon" />
		</span>
		<span
			v-if="!isLoading"
			class="c-button__text text-m-medium"
		>
			<slot />
		</span>
		<span
			v-else
			class="c-button__dots"
		>
			<span class="c-button__dot --first"></span>
			<span class="c-button__dot --middle"></span>
			<span class="c-button__dot --last"></span>
		</span>
	</button>
</template>

<script>
export default {
	name: 'c-button',

	inheritAttrs: false,

	props: {
		outlined: { type: Boolean },
		secondary: { type: Boolean },
		isLoading: { type: Boolean },
		fullWidth: {
			type: Boolean,
			default: true,
		},
	},

	computed: {
		classes({ outlined, secondary, fullWidth, isLoading }) {
			return {
				'--is-outlined': outlined,
				'--is-secondary': secondary,
				'--is-full-width': fullWidth,
				'--is-loading': isLoading,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.c-button {
	--focus-ring-radius: var(--border-radius-xl);
	display: flex;
	position: relative;
	color: RGB(var(--color-text-white));
	height: var(--btn-height, 54px);
	align-items: center;
	justify-content: center;
	padding: var(--spacing-l);
	border-radius: var(--border-radius-xl);
	letter-spacing: 0;
	line-height: 1.5;
	text-decoration: none;
	white-space: nowrap;
	background-color: RGB(var(--color-secondary));
	box-shadow: var(--elevation-medium);
}

.c-button.--is-full-width {
	width: 100%;
}

.c-button.--is-secondary:disabled,
.c-button:disabled {
	cursor: default;
	color: RGB(var(--color-accent-disabled));
	background-color: RGB(var(--color-accent-disabled-light));
}

.c-button:disabled .c-button__dot {
	background-color: RGB(var(--color-accent-disabled));
}

.c-button:not(.--is-outlined):disabled {
	box-shadow: none;
}

.c-button:not(:disabled):hover {
	background-color: RGB(var(--color-secondary-light));
	color: RGB(var(--color-text-primary));
}

.c-button:not(:disabled) .c-button:not(:disabled):active {
	box-shadow: var(--elevation-low);
}

.c-button.--is-secondary {
	color: RGB(var(--color-text-primary));
	background-color: RGB(var(--color-secondary-light));
}

.c-button.--is-secondary:not(:disabled):hover {
	background-color: RGB(var(--color-secondary));
	color: RGB(var(--color-text-white));
}

.c-button.--is-outlined {
	height: 44px;
	line-height: 1;
	color: RGB(var(--color-text-primary));
	background-color: RGB(var(--color-surface-light));
	box-shadow: 0px 0px 0px 1px RGB(var(--color-secondary-light));
}

.c-button.--is-outlined .c-button__icon {
	color: RGB(var(--color-secondary-light));
}

.c-button.--is-outlined .c-button__dot {
	background-color: RGB(var(--color-secondary));
}

.c-button.--is-outlined:disabled {
	box-shadow: 0px 0px 0px 1px RGB(var(--color-accent-disabled));
}

.c-button.--is-outlined:disabled .c-button__icon {
	color: RGB(var(--color-accent-disabled));
}

.c-button.--is-outlined:disabled .c-button__dot {
	background-color: RGB(var(--color-accent-disabled));
}

.c-button.--is-outlined:not(:disabled):hover .c-button__icon {
	color: RGB(var(--color-text-primary));
}

.c-button__icon {
	color: RGB(var(--color-button-icon, var(--color-text-primary)));
	display: flex;
	padding-right: var(--spacing-m);
}

.c-button__dots {
	display: flex;

	.c-button__dot:not(:last-of-type) {
		margin-right: var(--spacing-s);
	}
}

.c-button__dot {
	content: '';
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: var(--border-radius-full);
	background-color: RGB(var(--color-text-white));
	animation: dot-flashing 500ms infinite alternate;

	&.--first {
		animation-delay: 0ms;
	}

	&.--middle {
		animation-delay: 150ms;
	}

	&.--last {
		animation-delay: 300ms;
	}
}

@keyframes dot-flashing {
	0% {
		transform: translateY(2px);
		opacity: 0.1;
	}

	100% {
		transform: translateY(-2px);
		opacity: 0.9;
	}
}
</style>
